import React from 'react';
import PropTypes from 'prop-types';
import {observer, inject} from 'mobx-react';
import {PageTitle, history} from 'fn-common-ui';
import {computed, observable, autorun, makeObservable} from 'mobx';
import {isAlive} from 'mobx-state-tree';
import {Link, useParams, useNavigate} from 'react-router-dom';

import Alert from '../components/alert/alert';
import TextInput from '../components/forms/text-input';
import CheckboxGroup from '../components/forms/checkbox-group';
import Dropzone from '../components/dropzone/dropzone';
import ImageDropzone from '../components/dropzone/image-dropzone';
import DiscardChangesModal from '../components/modals/discard-changes-modal';
import LoadingOverlayWithCancel from '../components/loading/loading-overlay-with-cancel';
import { apiService } from '../core/services/api.service';
import Modal from '../components/modals/modal';


const ALL_MODES = ['add', 'edit'];

@inject('store')
@observer
class AddEditPage extends React.Component {
  static propTypes = {
    store: PropTypes.object.isRequired,
    match: PropTypes.object
  }

  constructor(props) {
    super(props);
    makeObservable(this);
    this.store = this.props.store;
    this.store.newMasterStore.Roles.fetchRoles();
    this.contentItems = this.props.store.newMasterStore.ContentItems;
    this.contentItem = {};
    this.alert = this.props.store.newMasterStore.alert;
    this.routeBack = '/admin';
    this.state = { uploadingFiles : false } ;

    // Copy route params into local fields so they can be used in computed/autorun
    this.routeParams = this.props.match.params;

    autorun(() => {
      if(this.contentItems.contentItemList.length > 0 && !this.contentItem.id) {
        if(this.mode === 'add') {
          this.contentItems.setupContentItemToAdd(parseInt(this.routeParams.sectionId));
          this.contentItem = this.contentItems.addingContentItem;
        } else {
          this.contentItems.setupContentItemToEdit(this.routeParams.contentItemId);
          this.contentItem = this.contentItems.editingContentItem;
        }
        this.readyToRenderForm = true;
      }
    })
  }

  @observable readyToRenderForm = false;

  @computed get mode() {
    const modeFromProps = this.routeParams.mode;
    if(ALL_MODES.indexOf(modeFromProps.toLowerCase()) > -1) {
      return modeFromProps.toLowerCase()
    } else {
      return 'add';
    }
  }

  @computed get contentType() {
    const sectionIdFromProps = this.routeParams.sectionId;
    return this.contentItems.getContentTypeFromSectionId(sectionIdFromProps);
  }

  componentWillUnmount() {
    apiService.clearFilesToUpload();
  }

  saveChanges = () => {
    const successMessage = this.mode === 'add'
      ? `Your ${this.contentType.toLowerCase()} has been added successfully.`
      : 'Your changes have been saved.';

    const success = () => {
      this.props.store.uploadStore.fullyDeleteRemovedFiles();
      this.contentItems.clearForm();
      this.props.navigate(this.routeBack, { state: { alertType: 'SUCCESS', alertText: successMessage } });
    }
    const fail = () => {
      this.alert.showAlert({type: 'ERROR', text: 'An error occured, please try again.'})
    }

    this.saveModal.hideModal();

    if(this.contentItem.isValid) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.setState({uploadingFiles : true});
      apiService.actualUploadOfFiles().then(() => {
        this.setState({uploadingFiles : false});
        if(this.mode === 'add') {
          this.contentItems.pushContentItemCreation().then(success, fail)
        } else {
          this.contentItems.pushContentItemEdits().then(success, fail)
        }
      }, () => {
        this.setState({uploadingFiles : false});
        this.alert.showAlert({type: 'ERROR', text: 'An error occured while uploading files, please try again.'});
      })
    }
  }

  discardChanges = () => {
    this.contentItems.clearForm();
    // remove any files that were uploaded but not used because we are discarding.
    this.props.store.uploadStore.removeUnusedUploads();
  }

  renderActions = (location) => {
    const saveBtnClass = (this.contentItem.isValid && this.contentItems.formIsDirty) ? '' : 'disabled';
    const typeString = this.contentType.replace('_',' ').toLowerCase();
    const saveString = this.mode === 'add' ? `Add ${typeString}` : 'Save changes';
    const discardString = 'Discard & go back';
    const capitalizedMode = this.mode.charAt(0).toUpperCase() + this.mode.substr(1);
    if(location === 'top') {
      return (<React.Fragment>
        <div className="subheader__title" >
          <PageTitle>
            {`${capitalizedMode}ing ${this.mode === 'add' ? 'new' : ''} ${typeString}`}
          </PageTitle>
        </div>
        <div className="subheader__actions">
          <button className={saveBtnClass} onClick={()=>this.saveModal.showModal()}>
            <i className={this.mode === 'add' ? 'icon-upload' : 'icon-save'} aria-hidden={true} />
            &nbsp;{saveString}
          </button>
          <Link to={this.routeBack} className="as-btn">
            <i className="icon-arrow-circle-left" aria-hidden={true} />
            &nbsp;{discardString}
          </Link>
        </div>
      </React.Fragment>)
    } else if(location === 'bottom') {
      return (<div className="button-row">
        <button className={`fn-primary ${saveBtnClass}`} onClick={()=>this.saveModal.showModal()}>
          {saveString}
        </button>
        <Link to={this.routeBack} className="fn-secondary">
          {discardString}
        </Link>
      </div>)
    }
  }

  renderField = (key) => {
    let inputInfo = this.contentItem.getFieldInfo(key);
    inputInfo.key = key;
    inputInfo.id = this.contentItem.id + key;

    if(inputInfo.type === 'DROPZONE') {
      const existingFile = this.contentItem[key];
      const existingImage = this.contentItem[key] ? existingFile : null;
      return inputInfo.isImageUpload
        ? <ImageDropzone
            key={key}
            id={this.contentItem.id+key}
            existingFile={{imageUrl: existingImage}}
            onChange={(url) => {
              this.contentItem.changeProperty(key, url);
              this.contentItems.setFormWasTouched();
            }}
            {...inputInfo} />
        : <Dropzone
            key={key}
            id={this.contentItem.id+key}
            existingFile={{filename: existingFile}}
            onChange={(filename) => {
              this.contentItem.changeProperty(key, filename);
              this.contentItems.setFormWasTouched();
            }}
            {...inputInfo} />
    } else {
      if(inputInfo.labelText!='Keywords'){
      return <TextInput
        key={key}
        id={key}
        value={this.contentItem[key] || ''}
        onChange={(key, val) => {
          this.contentItem.changeProperty(key,val);
          this.contentItems.setFormWasTouched();
        }}
        required={true}
        type={inputInfo.type}
        labelText={inputInfo.labelText}
        helperText={inputInfo.helperText}
        errorMessage={inputInfo.errorMessage}
        charLimit={inputInfo.charLimit}
        announceError={true} />
      }
        else{
          return <TextInput
        key={key}
        id={key}
        value={this.contentItem[key] || ''}
        onChange={(key, val) => {
          this.contentItem.changeProperty(key,val);
          this.contentItems.setFormWasTouched();
        }}
        required={false}
        type={inputInfo.type}
        labelText={inputInfo.labelText}
        helperText={inputInfo.helperText}
        errorMessage={inputInfo.errorMessage}
        charLimit={inputInfo.charLimit}
        announceError={true} />
        }

    }
  }

  renderProperFieldsForType = () => {
    return this.contentItem && this.contentItem.fieldList && this.contentItem.fieldList.length > 0
      ? this.contentItem.fieldList.map(key => this.renderField(key))
      : null;
  }

  renderAuthorizationCheckboxes = () => {
    if(!isAlive(this.contentItem)) return null; // FPSE-3250 - if the content item isn;t still in the model treel, don't render it. this is a hack. But it works.
    if(this.contentItem.type === 'TOP_BANNER' || this.contentItem.type === 'BOTTOM_BANNER') return null;
    return (
      <CheckboxGroup
        id="authorizations"
        className="two-column-checkbox-list"
        value={this.contentItem.authorizationsAsStrings}
        labelText="Select role(s) that can see this content"
        required={true}
        showSelectAll={true}
        onChange={(id, value) => {
          const parsedValue = value.map(stringVal => JSON.parse(stringVal));
          this.contentItem.changeProperty(id, parsedValue);
          this.contentItems.setFormWasTouched();
        }}
        optionsList={this.contentItems.authorizationOptionsList} />
    )
  }

  hideUploadingOverlay = () => {
    this.setState({uploadingFiles: false});
  }

  render() {
    return (
      <section id="content-edit-page">
        <LoadingOverlayWithCancel
          show={this.contentItems.showLoadingOverlay}
          cancel={this.contentItems.cancelSubmission}
          actionText="Saving Changes" />
        <LoadingOverlayWithCancel
          show={this.state.uploadingFiles}
          cancel={this.hideUploadingOverlay}
          actionText="Uploading" />
        <div className="subheader">
          <div className="container">
            <div className="row">
              <div className="subheader__toolbar">
                {this.renderActions('top')}
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row">
            <div className="col-12 col-sm-8 col-lg-6">
              <Alert {...this.alert} onHide={this.alert.hideAlert} />
              <form>
                {this.readyToRenderForm && this.renderProperFieldsForType()}
                {this.readyToRenderForm && this.renderAuthorizationCheckboxes()}
              </form>
              {this.renderActions('bottom')}
            </div>
          </div>
        </div>
        <Modal
            id="confirm-modal"
            title={'Confirm Publish'}
            ref={i => this.saveModal = i}
            restoreFocusTo="#content-edit-page"
            primaryAction={this.saveChanges}
            primaryButtonLabel="Confirm"
            secondaryAction={() => this.saveModal.hideModal()}
            secondaryButtonLabel="Cancel">
            <p>These changes will get immediately published to production.</p>
        </Modal>
        <DiscardChangesModal
          isDirty={() => this.contentItems.formIsDirty}
          clearForm={this.discardChanges} />
      </section>
    )
  }
}

export default function AddEditPageWrapper(props) {
  const params = useParams();
  const navigate = useNavigate();
  return <AddEditPage {...props} match={{ params }} navigate={navigate} />;
}
